/**
 * Named tunings, kept on the machine.
 *
 * ### This is the one thing this project stores, and that is a reversal
 *
 * Everything else here is deliberately not persisted. Recordings live in memory
 * for the life of the tab because they are video of somebody's face and leaving
 * them on the machine long after the reason for making them has passed is not a
 * default worth having. A tuning used to be held to the same rule, on the
 * argument that the exception is how the rule ends.
 *
 * That was the wrong line to draw. A tuning is thirty numbers and a language
 * tag: it says nothing about who was in front of the camera, and nothing in it
 * survives being read by somebody else. What the rule was protecting was the
 * biometric data, and none of that is here. Meanwhile the cost of the rule was
 * real — the address bar is a fine way to *send* a tuning and a hopeless way to
 * *keep* one, and a performer was re-finding the same eleven values by ear every
 * time the page loaded.
 *
 * So: tunings persist, recordings still do not, and the README says so plainly.
 * The guard is what may be written rather than whether anything is —
 * `sanitise` drops everything that is not a primitive under a tunable path, so
 * this cannot quietly grow into a place where other things get put.
 *
 * ### Every read is defensive
 *
 * `localStorage` throws on access in iOS private browsing, is absent in a
 * worker, can be full, and can hold whatever a previous version of this file
 * wrote. None of that is worth an exception on a page whose job is to be
 * performed with, so every failure here degrades to "no presets" and the
 * instrument runs exactly as it did before this file existed.
 */

export const STORAGE_KEY = 'visualspeech.tuning';
export const STORE_VERSION = 1;

/** The longest a name may be. Long enough to be descriptive, short enough to fit a dropdown. */
export const NAME_MAX = 40;

/** An empty store, and the answer to every kind of failure below. */
export const emptyStore = () => ({ version: STORE_VERSION, last: null, lang: null, presets: {} });

/**
 * `localStorage`, or something shaped like it that does nothing.
 *
 * Returned rather than thrown so callers have no branch: a page with storage
 * disabled runs the same code as one without, and simply never remembers.
 */
export function safeStorage() {
  try {
    const s = globalThis.localStorage;
    // Touched rather than trusted. Safari exposes the object in private mode and
    // throws on write, so the only honest test is a write.
    const probe = `${STORAGE_KEY}.probe`;
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

/**
 * A tuning stripped to what may be persisted.
 *
 * Primitives under string keys and nothing else — no nested objects, no
 * functions, no `__proto__`. Unknown *paths* are deliberately left alone: this
 * file does not own the schema, and `applyTuning` already ignores a path it does
 * not recognise, which is what lets a preset written by an older build still set
 * the eleven controls it got right.
 *
 * @param {*} raw
 * @returns {Record<string, number|boolean|string>}
 */
export function sanitise(raw) {
  /** @type {Record<string, number|boolean|string>} */
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [key, value] of Object.entries(raw)) {
    if (key === '__proto__' || key === 'constructor' || !key) continue;
    const ok =
      (typeof value === 'number' && Number.isFinite(value)) ||
      typeof value === 'boolean' ||
      (typeof value === 'string' && value.length <= 64);
    if (ok) out[key] = value;
  }
  return out;
}

/** A name as it will be stored: trimmed, bounded, never empty. */
export function cleanName(raw) {
  const name = String(raw ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_MAX);
  return name || null;
}

/**
 * Parse whatever was in storage into a store that is safe to use.
 *
 * Never throws and never returns a partial shape. A store from a future version
 * is discarded rather than guessed at — the alternative is silently reinterpreting
 * somebody's saved settings under rules that have changed.
 */
export function parseStore(text) {
  try {
    const raw = JSON.parse(String(text ?? ''));
    if (!raw || typeof raw !== 'object' || raw.version !== STORE_VERSION) return emptyStore();
    const presets = {};
    for (const [name, tuning] of Object.entries(raw.presets ?? {})) {
      const key = cleanName(name);
      if (key) presets[key] = sanitise(tuning);
    }
    const last = sanitise(raw.last && typeof raw.last === 'object' ? raw.last : {});
    return {
      version: STORE_VERSION,
      // `last` is a tuning, not a name: what should come back on a refresh is
      // what was on screen, which is usually a preset with two things nudged
      // since — and a name would restore the preset and lose the nudges.
      last: Object.keys(last).length || raw.last === null ? last : {},
      // Kept apart from `last` on purpose. `last` is a diff against the shipped
      // defaults, so a language that *is* the default vanishes from it — and
      // "I explicitly chose English" becomes indistinguishable from "I never
      // chose", which is how a browser set to Portuguese kept overriding a
      // deliberate choice on every reload. A diff cannot express this, so it
      // does not have to.
      lang: typeof raw.lang === 'string' && raw.lang.length <= 32 ? raw.lang : null,
      presets,
    };
  } catch {
    return emptyStore();
  }
}

/**
 * Named tunings over a storage backend.
 *
 * The store is read once and held, then written whole on every change. It is a
 * few hundred bytes and there is exactly one writer, so nothing here is worth
 * the bug that incremental updates would eventually cost.
 */
export class Presets {
  /** @param {?Storage} [storage] defaults to localStorage when it is usable */
  constructor(storage = safeStorage()) {
    this.storage = storage;
    this.store = emptyStore();
    if (!storage) return;
    try {
      this.store = parseStore(storage.getItem(STORAGE_KEY));
    } catch {
      this.store = emptyStore();
    }
  }

  /** Whether anything written here will still be here next time. */
  get available() {
    return Boolean(this.storage);
  }

  _flush() {
    if (!this.storage) return false;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.store));
      return true;
    } catch {
      // Full, or private mode changed its mind. The in-memory store stays
      // correct for this tab; only the persistence is lost.
      return false;
    }
  }

  /** Saved names, in a stable order so the dropdown does not reshuffle. */
  names() {
    return Object.keys(this.store.presets).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }

  /** @returns {?Record<string, number|boolean|string>} a copy, never the stored object */
  get(name) {
    const key = cleanName(name);
    const found = key && this.store.presets[key];
    return found ? { ...found } : null;
  }

  has(name) {
    return Boolean(this.get(name));
  }

  /** @returns {?string} the name it was stored under, or null if the name was empty */
  put(name, tuning) {
    const key = cleanName(name);
    if (!key) return null;
    this.store.presets[key] = sanitise(tuning);
    this._flush();
    return key;
  }

  remove(name) {
    const key = cleanName(name);
    if (!key || !(key in this.store.presets)) return false;
    delete this.store.presets[key];
    this._flush();
    return true;
  }

  /** What was on screen when the tab was last used. */
  get last() {
    return { ...this.store.last };
  }

  setLast(tuning) {
    this.store.last = sanitise(tuning);
    this._flush();
  }

  /** The language deliberately chosen here, default-valued or not. */
  get lang() {
    return this.store.lang;
  }

  setLang(tag) {
    const value = typeof tag === 'string' && tag.length <= 32 ? tag : null;
    if (value === this.store.lang) return;
    this.store.lang = value;
    this._flush();
  }

  /** Everything this file ever wrote, gone. */
  clear() {
    this.store = emptyStore();
    if (!this.storage) return;
    try {
      this.storage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do: the in-memory store is already empty.
    }
  }
}

export default Presets;
